import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Plus,
  Clock,
  CheckCircle2,
  FileEdit,
  AlertTriangle,
  Eye,
  Edit3,
  X,
} from 'lucide-react';
import { postsAPI } from '../../services/api';

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  status: string;
  platforms: string[];
  media_count: number;
}

const getStatusConfig = (t: (key: string, fallback: string) => string): Record<string, { label: string; bg: string; backgroundColor: string; color: string; text: string; dot: string; icon: React.ElementType }> => ({
  scheduled: { label: t('cal_status_scheduled', 'Scheduled'), bg: 'bg-n-blue-bg', text: 'text-n-blue', backgroundColor: '#d3e5ef', color: '#183347', dot: '#337ea9', icon: Clock },
  published: { label: t('cal_status_published', 'Published'), bg: 'bg-n-green-bg', text: 'text-n-green', backgroundColor: '#dbeddb', color: '#1c3829', dot: '#448361', icon: CheckCircle2 },
  draft: { label: t('cal_status_draft', 'Draft'), bg: 'bg-n-hover', text: 'text-n-text-md', backgroundColor: '#e3e2e0', color: '#32302c', dot: '#9b9a97', icon: FileEdit },
  failed: { label: t('cal_status_failed', 'Failed'), bg: 'bg-n-red-bg', text: 'text-n-red', backgroundColor: '#ffe2dd', color: '#5d1715', dot: '#d44c47', icon: AlertTriangle },
});

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'bg-blue-600', instagram: 'bg-pink-600', twitter: 'bg-black',
  tiktok: 'bg-gray-900', linkedin: 'bg-blue-700', youtube: 'bg-red-600',
  pinterest: 'bg-red-700', threads: 'bg-gray-900', bluesky: 'bg-blue-400',
  telegram: 'bg-blue-500', tumblr: 'bg-blue-600', wordpress: 'bg-gray-700', gmb: 'bg-red-500',
};

const PLATFORM_ICONS: Record<string, string> = {
  facebook: 'f', instagram: '📷', twitter: '𝕏', tiktok: '♪', linkedin: 'in',
  youtube: '▶', pinterest: 'P', threads: '@', bluesky: '🦋', telegram: '✈',
  tumblr: 'T', wordpress: 'W', gmb: 'G',
};

type ViewMode = 'month' | 'week';

export const CalendarPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  // Schedule picker state
  const [schedulePicker, setSchedulePicker] = useState<{ day: number; date: string } | null>(null);
  const [pickerTime, setPickerTime] = useState('09:00');

  // Accessibility IDs
  const scheduleDateId = React.useId();
  const scheduleTimeId = React.useId();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const startDate = viewMode === 'month'
    ? new Date(year, month, 1).toISOString().split('T')[0]
    : (() => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() - d.getDay());
        return d.toISOString().split('T')[0];
      })();

  const endDate = viewMode === 'month'
    ? new Date(year, month + 1, 0).toISOString().split('T')[0]
    : (() => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + (6 - d.getDay()));
        return d.toISOString().split('T')[0];
      })();

  const { data: calendarData, isLoading } = useQuery({
    queryKey: ['calendar', startDate, endDate],
    queryFn: () => postsAPI.calendar(startDate, endDate).then(r => r.data),
    staleTime: 60_000,
  });

  const events: CalendarEvent[] = calendarData?.events || [];

  const getDaysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const today = new Date();
  const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;

  const handlePrev = () => {
    if (viewMode === 'month') setCurrentDate(new Date(year, month - 1));
    else { const d = new Date(currentDate); d.setDate(d.getDate() - 7); setCurrentDate(d); }
  };

  const handleNext = () => {
    if (viewMode === 'month') setCurrentDate(new Date(year, month + 1));
    else { const d = new Date(currentDate); d.setDate(d.getDate() + 7); setCurrentDate(d); }
  };

  const handleToday = () => setCurrentDate(new Date());

  const getEventsForDay = (day: number): CalendarEvent[] => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.start?.startsWith(dateStr));
  };

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return events.filter(e => e.start?.startsWith(dateStr));
  };

  const isTodayDay = (day: number) => isCurrentMonth && today.getDate() === day;

  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);
  while (calendarDays.length % 7 !== 0) calendarDays.push(null);

  const getWeekDates = (): Date[] => {
    const dates: Date[] = [];
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    for (let i = 0; i < 7; i++) { const d = new Date(start); d.setDate(d.getDate() + i); dates.push(d); }
    return dates;
  };

  const weekLabel = (() => {
    const dates = getWeekDates();
    const s = dates[0]; const e = dates[6];
    const sMonth = s.toLocaleDateString('en-US', { month: 'short' });
    const eMonth = e.toLocaleDateString('en-US', { month: 'short' });
    return sMonth === eMonth
      ? `${sMonth} ${s.getDate()} – ${e.getDate()}, ${s.getFullYear()}`
      : `${sMonth} ${s.getDate()} – ${eMonth} ${e.getDate()}, ${e.getFullYear()}`;
  })();

  // Open the schedule picker for a given day
  const openSchedulePicker = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSchedulePicker({ day, date: dateStr });
    setPickerTime('09:00');
  };

  // Confirm: navigate to compose with selected date+time
  const confirmSchedule = () => {
    if (!schedulePicker) return;
    const dateTimeStr = `${schedulePicker.date}T${pickerTime}`;
    navigate(`/compose?schedule=${encodeURIComponent(dateTimeStr)}`);
    setSchedulePicker(null);
  };

  const scheduledCount = events.filter(e => e.status === 'scheduled').length;
  const publishedCount = events.filter(e => e.status === 'published').length;
  const draftCount = events.filter(e => e.status === 'draft').length;

  const STATUS_CONFIG = useMemo(() => getStatusConfig(t), [t]);

  return (
    <div className="bg-n-card h-full flex flex-col">
      {/* Notion-style header */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-4xl">📅</span>
          <h1 className="text-4xl font-bold text-n-text">{t('cal_calendar', 'Calendar')}</h1>
        </div>
        <p className="text-sm text-n-text-md">{t('cal_manage_posts', 'Manage and schedule your posts')}</p>
      </div>

      <div className="px-4 pt-3 pb-2 flex flex-col flex-1 min-h-0">

        {/* Compact toolbar: nav + stats + view toggle + new post */}
        <div className="flex items-center gap-2 mb-2 flex-shrink-0">
          {/* Date navigation */}
          <button onClick={handlePrev} aria-label={t('cal_prev_period', 'Previous period')} className="p-1.5 rounded-lg transition hover:bg-n-hover text-n-text-md">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h2 className="text-sm font-bold text-n-text min-w-[140px] text-center">
            {viewMode === 'month' ? monthName : weekLabel}
          </h2>
          <button onClick={handleNext} aria-label={t('cal_next_period', 'Next period')} className="p-1.5 rounded-lg transition hover:bg-n-hover text-n-text-md">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={handleToday} className="px-2.5 py-1 text-[10px] font-semibold border border-n-border rounded-lg text-n-text-md hover:bg-n-hover transition ml-1">
            {t('cal_today', 'Today')}
          </button>

          {/* Stats */}
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-n-blue-bg text-n-blue">
              <Clock className="w-2.5 h-2.5" /> {scheduledCount}
            </span>
            <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-n-green-bg text-n-green">
              <CheckCircle2 className="w-2.5 h-2.5" /> {publishedCount}
            </span>
            {draftCount > 0 && (
              <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-n-hover text-n-text-md">
                <FileEdit className="w-2.5 h-2.5" /> {draftCount}
              </span>
            )}
          </div>

          {/* View toggle */}
          <div className="flex gap-px p-0.5 bg-n-hover rounded-lg">
            <button
              onClick={() => setViewMode('month')}
              aria-label={t('cal_month_view', 'Month view')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${viewMode === 'month' ? 'bg-[#2383e2] text-white shadow-sm' : 'text-n-text-md hover:text-n-text'}`}
            >
              {t('cal_month', 'Month')}
            </button>
            <button
              onClick={() => setViewMode('week')}
              aria-label={t('cal_week_view', 'Week view')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${viewMode === 'week' ? 'bg-[#2383e2] text-white shadow-sm' : 'text-n-text-md hover:text-n-text'}`}
            >
              {t('cal_week', 'Week')}
            </button>
          </div>

          {/* New Post */}
          <button
            onClick={() => navigate('/compose')}
            aria-label={t('cal_create_post', 'Create new post')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#2383e2] rounded-lg transition-all hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" /> {t('cal_new_post', 'New Post')}
          </button>
        </div>

        {/* Calendar Body */}
        <div className="rounded-n border border-n-border overflow-hidden flex-1 flex flex-col min-h-0 shadow-n-sm">

          <div className="p-2 flex-1 flex flex-col min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-n-border rounded-full animate-spin" style={{ borderTopColor: '#2383e2' }} />
              </div>
            ) : viewMode === 'month' ? (
              <div className="flex flex-col flex-1 min-h-0">
                {/* Day Headers */}
                <div className="grid grid-cols-7 flex-shrink-0">
                  {[
                    t('cal_day_sun', 'Sun'),
                    t('cal_day_mon', 'Mon'),
                    t('cal_day_tue', 'Tue'),
                    t('cal_day_wed', 'Wed'),
                    t('cal_day_thu', 'Thu'),
                    t('cal_day_fri', 'Fri'),
                    t('cal_day_sat', 'Sat'),
                  ].map((day, idx) => (
                    <div key={idx} className="text-center text-[10px] font-semibold text-n-text-lt uppercase tracking-wider py-1.5">{day}</div>
                  ))}
                </div>

                {/* Days Grid - fills remaining height */}
                <div className="flex-1 grid grid-cols-7 gap-px bg-n-border rounded-lg overflow-hidden" style={{ gridTemplateRows: `repeat(${Math.ceil(calendarDays.length / 7)}, 1fr)` }}>
                  {calendarDays.map((day, idx) => {
                    const dayEvents = day !== null ? getEventsForDay(day) : [];
                    const dayIsToday = day !== null && isTodayDay(day);
                    const isHovered = day !== null && hoveredDay === day;

                    return (
                      <div
                        key={idx}
                        className={`p-1.5 transition-all relative overflow-hidden ${
                          day === null ? 'bg-n-page' : dayIsToday ? 'bg-n-card' : 'bg-n-card hover:bg-n-hover'
                        }`}
                        style={dayIsToday ? { boxShadow: 'inset 0 2px 0 0 #2383e2' } : undefined}
                        onMouseEnter={() => day !== null && setHoveredDay(day)}
                        onMouseLeave={() => setHoveredDay(null)}
                      >
                        {day !== null && (
                          <>
                            <div className="flex items-center justify-between mb-1">
                              <span
                                className={`text-xs font-semibold w-5 h-5 flex items-center justify-center rounded-full ${dayIsToday ? 'text-white' : 'text-n-text'}`}
                                style={dayIsToday ? { backgroundColor: '#2383e2' } : undefined}
                              >
                                {day}
                              </span>
                              {isHovered && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openSchedulePicker(day); }}
                                  className="w-4 h-4 flex items-center justify-center rounded-full transition-all hover:scale-110"
                                  style={{ backgroundColor: '#2383e220', color: '#2383e2' }}
                                  title={t('cal_schedule_post', 'Schedule a post')}
                                  aria-label={t('cal_schedule_on_date', `Schedule a post on ${monthName} ${day}`)}
                                >
                                  <Plus className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </div>

                            <div className="space-y-0.5">
                              {dayEvents.slice(0, 3).map(event => {
                                const config = STATUS_CONFIG[event.status] || STATUS_CONFIG.draft;
                                return (
                                  <button
                                    key={event.id}
                                    onClick={() => setSelectedEvent(event)}
                                    className={`w-full text-left text-[9px] px-1.5 py-0.5 rounded-n-xs font-medium truncate block transition-all ${config.bg} ${config.text}`}
                                    title={event.title}
                                    aria-label={`View post: ${event.title}`}
                                  >
                                    {event.title}
                                  </button>
                                );
                              })}
                              {dayEvents.length > 3 && (
                                <p className="text-[9px] font-medium text-n-text-lt px-1">{t('cal_more_posts', `+${dayEvents.length - 3} more`)}</p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* WEEK VIEW */
              <div className="flex flex-col flex-1 min-h-0">
                {/* Day headers row */}
                <div className="grid grid-cols-7 gap-px bg-n-border rounded-t-lg overflow-hidden flex-shrink-0">
                  {getWeekDates().map((date, i) => {
                    const dayIsToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
                    return (
                      <div key={i} className="text-center py-2 bg-n-card" style={dayIsToday ? { boxShadow: 'inset 0 2px 0 0 #2383e2' } : undefined}>
                        <div className="text-[10px] font-semibold text-n-text-lt uppercase">
                          {date.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div
                          className={`text-sm font-bold mt-0.5 w-6 h-6 flex items-center justify-center mx-auto rounded-full ${dayIsToday ? 'text-white' : 'text-n-text'}`}
                          style={dayIsToday ? { backgroundColor: '#2383e2' } : undefined}
                        >
                          {date.getDate()}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Scrollable event columns */}
                <div className="flex-1 grid grid-cols-7 gap-px bg-n-border rounded-b-lg overflow-hidden min-h-0">
                  {getWeekDates().map((date, i) => {
                    const dateEvents = getEventsForDate(date);
                    return (
                      <div
                        key={i}
                        className="p-1.5 bg-n-card overflow-y-auto"
                      >
                        {dateEvents.length === 0 ? (
                          <div className="flex items-center justify-center h-full text-n-text-lt">
                            <p className="text-[10px]">{t('cal_no_posts', 'No posts')}</p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {dateEvents.map(event => {
                              const config = STATUS_CONFIG[event.status] || STATUS_CONFIG.draft;
                              const StatusIcon = config.icon;
                              return (
                                <button
                                  key={event.id}
                                  onClick={() => setSelectedEvent(event)}
                                  aria-label={`View post: ${event.title}`}
                                  className={`w-full text-left p-1.5 rounded-n-xs ${config.bg} transition hover:opacity-80`}
                                >
                                  <div className="flex items-center gap-1 mb-0.5">
                                    <StatusIcon className={`w-3 h-3 ${config.text}`} />
                                    <span className={`text-[9px] font-semibold ${config.text} uppercase`}>{config.label}</span>
                                  </div>
                                  <p className={`text-[10px] font-medium ${config.text} truncate`}>{event.title}</p>
                                  {event.platforms?.length > 0 && (
                                    <div className="flex gap-0.5 mt-1">
                                      {event.platforms.slice(0, 3).map(p => (
                                        <span key={p} className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold ${PLATFORM_COLORS[p] || 'bg-n-text-lt'}`} title={p}>
                                          {PLATFORM_ICONS[p]?.charAt(0) || '?'}
                                        </span>
                                      ))}
                                      {event.platforms.length > 3 && (
                                        <span className="text-[8px] text-n-text-lt ml-0.5">+{event.platforms.length - 3}</span>
                                      )}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============ SCHEDULE PICKER MODAL ============ */}
      {schedulePicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          role="presentation"
          onClick={() => setSchedulePicker(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setSchedulePicker(null); } }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            className="bg-n-card rounded-n border border-n-border w-full max-w-sm overflow-hidden shadow-n"
            role="dialog"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-n-border flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-n-text">{t('cal_schedule_modal_title', 'Schedule a Post')}</h3>
                <p className="text-sm text-n-text-md mt-0.5">
                  {new Date(schedulePicker.date + 'T00:00').toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                  })}
                </p>
              </div>
              <button onClick={() => setSchedulePicker(null)} className="text-n-text-lt hover:text-n-text-md">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Date (read-only, shows the selected day) */}
              <div>
                <label htmlFor={scheduleDateId} className="block text-sm font-medium text-n-text mb-1">{t('cal_date', 'Date')}</label>
                <input
                  id={scheduleDateId}
                  type="date"
                  value={schedulePicker.date}
                  onChange={(e) => setSchedulePicker(prev => prev ? { ...prev, date: e.target.value } : null)}
                  className="w-full px-3 py-2.5 bg-n-page border border-n-border rounded-n-sm text-sm text-n-text focus:outline-none focus:ring-2 focus:ring-opacity-30 focus:ring-[#2383e2]"
                />
              </div>

              {/* Time picker */}
              <div>
                <label htmlFor={scheduleTimeId} className="block text-sm font-medium text-n-text mb-1">{t('cal_time', 'Time')}</label>
                <input
                  id={scheduleTimeId}
                  type="time"
                  value={pickerTime}
                  onChange={(e) => setPickerTime(e.target.value)}
                  className="w-full px-3 py-2.5 bg-n-page border border-n-border rounded-n-sm text-sm text-n-text focus:outline-none focus:ring-2 focus:ring-opacity-30 focus:ring-[#2383e2]"
                />
              </div>

              {/* Quick time presets */}
              <div>
                <p className="text-xs font-medium text-n-text-md mb-2">{t('cal_quick_select', 'Quick select')}</p>
                <div className="flex flex-wrap gap-2">
                  {['06:00', '09:00', '12:00', '15:00', '18:00', '21:00'].map(timeStr => (
                    <button
                      key={timeStr}
                      onClick={() => setPickerTime(timeStr)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-n-sm border transition ${
                        pickerTime === timeStr
                          ? 'bg-[#2383e2] text-white border-[#2383e2]'
                          : 'text-n-text-md border-n-border hover:bg-n-hover'
                      }`}
                    >
                      {new Date(`2000-01-01T${timeStr}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-n-border flex justify-end gap-3">
              <button
                onClick={() => setSchedulePicker(null)}
                className="px-4 py-2 text-sm font-medium text-n-text bg-n-card border border-n-border hover:bg-n-hover rounded-n-sm transition"
              >
                {t('cal_cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmSchedule}
                className="px-5 py-2 text-sm font-medium text-white bg-[#2383e2] rounded-n-sm transition hover:opacity-90"
              >
                {t('cal_create_post_btn', 'Create Post')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ EVENT DETAIL MODAL ============ */}
      {selectedEvent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          role="button"
          tabIndex={-1}
          aria-label="Close event detail"
          onClick={() => setSelectedEvent(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setSelectedEvent(null); } }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div className="bg-n-card rounded-n border border-n-border max-w-md w-full overflow-hidden shadow-n" role="dialog" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-n-border">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {(() => {
                      const config = STATUS_CONFIG[selectedEvent.status] || STATUS_CONFIG.draft;
                      return (
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-n-xs ${config.bg} ${config.text}`}>
                          <config.icon className="w-3 h-3" />
                          {config.label}
                        </span>
                      );
                    })()}
                  </div>
                  <h3 className="text-lg font-bold text-n-text truncate">{selectedEvent.title}</h3>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="text-n-text-lt hover:text-n-text-md ml-3">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-n-text-lt" />
                <p className="font-medium text-n-text">
                  {selectedEvent.start
                    ? new Date(selectedEvent.start).toLocaleDateString('en-US', {
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                      })
                    : t('cal_no_date_set', 'No date set')}
                </p>
              </div>

              {selectedEvent.platforms?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-n-text-md mb-2">{t('cal_publishing_to', 'Publishing to')}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedEvent.platforms.map(p => (
                      <span key={p} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-n-sm text-white text-xs font-medium ${PLATFORM_COLORS[p] || 'bg-n-text-lt'}`}>
                        <span>{PLATFORM_ICONS[p] || '?'}</span>
                        <span className="capitalize">{p}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedEvent.media_count > 0 && (
                <p className="text-xs text-n-text-md">
                  {t('cal_media_attached', '{{count}} media file{{plural}} attached', { count: selectedEvent.media_count, plural: selectedEvent.media_count > 1 ? 's' : '' })}
                </p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-n-border flex gap-2">
              <button
                onClick={() => { navigate(`/compose/${selectedEvent.id}`); setSelectedEvent(null); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#2383e2] rounded-n-sm transition hover:opacity-90"
              >
                <Edit3 className="w-4 h-4" /> {t('cal_edit_post', 'Edit Post')}
              </button>
              <button
                onClick={() => { navigate(`/posts?highlight=${selectedEvent.id}`); setSelectedEvent(null); }}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border border-n-border text-n-text bg-n-card rounded-n-sm hover:bg-n-hover transition"
              >
                <Eye className="w-4 h-4" /> {t('cal_view', 'View')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
